import { create } from 'zustand';
import type { FindView, NothingReason, ResolveResult } from '../models/types';
import { api } from '../services/api';
import { ApiException } from '../services/apiClient';
import { currentPosition } from '../services/geolocation';
import { Logger } from '../services/logger';
import { POOR_SIGNAL_MESSAGE } from '../services/messages';
import { usePlayer } from './player';

const log = new Logger('Scan');

export type ScanPhase = 'idle' | 'scanning' | 'found' | 'nothing' | 'resolving' | 'error';

/** Client-side mirror of the server cooldown so the button disables immediately. */
export const SCAN_COOLDOWN_SECONDS = 30;

const nowSeconds = () => Math.floor(Date.now() / 1000);

interface ScanState {
  phase: ScanPhase;
  find: FindView | null;
  nothingReason: NothingReason | null;
  nextWindowAt: number | null;
  /** Unix seconds until which the Scan button is disabled. */
  cooldownUntil: number | null;
  errorMessage: string | null;
  lastResolve: ResolveResult | null;
  scan: () => Promise<void>;
  resolve: (opts: { pickup: boolean }) => Promise<ResolveResult | null>;
  dismiss: () => void;
  reset: () => void;
}

export const isBusy = (s: Pick<ScanState, 'phase'>): boolean => s.phase === 'scanning' || s.phase === 'resolving';
export const cooldownRemaining = (cooldownUntil: number | null, now: number): number =>
  cooldownUntil == null ? 0 : Math.max(0, cooldownUntil - now);

const initial = {
  phase: 'idle' as ScanPhase,
  find: null,
  nothingReason: null,
  nextWindowAt: null,
  cooldownUntil: null,
  errorMessage: null,
  lastResolve: null,
};

export const useScan = create<ScanState>((set, get) => ({
  ...initial,

  scan: async () => {
    if (isBusy(get())) return;
    set({ phase: 'scanning', find: null, errorMessage: null });
    try {
      const pos = await currentPosition();
      const result = await api.scan({ lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy });
      const cooldownUntil = nowSeconds() + SCAN_COOLDOWN_SECONDS;
      if (result.kind === 'found') {
        log.info('found', { itemId: result.find.itemId, rarity: result.find.rarity, partId: result.find.partId });
        set({ phase: 'found', find: result.find, cooldownUntil });
      } else {
        set({ phase: 'nothing', nothingReason: result.reason, nextWindowAt: result.nextWindowAt, cooldownUntil });
      }
      // Scan updates stats on the profile; realtime will also push this.
      void usePlayer.getState().refresh();
    } catch (e) {
      if (e instanceof ApiException) {
        if (e.isCooldown) {
          set({ phase: 'idle', cooldownUntil: nowSeconds() + (e.retryAfterSeconds ?? SCAN_COOLDOWN_SECONDS) });
        } else if (e.isAccuracyTooLow) {
          set({ phase: 'error', errorMessage: POOR_SIGNAL_MESSAGE });
        } else {
          log.warn('scan failed', { error: e });
          set({ phase: 'error', errorMessage: e.message });
        }
      } else {
        log.error('scan failed', { error: e });
        set({ phase: 'error', errorMessage: 'Could not scan right now. Check your connection and GPS.' });
      }
    }
  },

  resolve: async ({ pickup }) => {
    const find = get().find;
    if (!find || isBusy(get())) return null;
    set({ phase: 'resolving' });
    try {
      const result = await api.resolveFind(find.findId, { pickup });
      set({ phase: 'idle', find: null, lastResolve: result });
      void usePlayer.getState().refresh();
      return result;
    } catch (e) {
      if (e instanceof ApiException && e.isHuntExpired) {
        set({ phase: 'error', find: null, errorMessage: 'That hunt ran out of time.' });
        // The hunt is gone server-side; drop it from the screen too.
        void usePlayer.getState().refresh();
      } else if (e instanceof ApiException) {
        set({ phase: 'error', find: null, errorMessage: e.isFindNotFound ? 'That find drifted away. Scan again!' : e.message });
      } else {
        log.error('resolve failed', { error: e });
        set({ phase: 'error', errorMessage: 'Could not reach the Ethr. Try again.' });
      }
      return null;
    }
  },

  dismiss: () => set({ phase: 'idle', find: null, errorMessage: null }),
  reset: () => set({ ...initial }),
}));
